import { hierarchy, treemap, treemapResquarify } from "d3-hierarchy";

import type { AppLeaf, CategoryNode } from "./types";

// Internal breathing room reserved INSIDE every block so a label never runs to
// the clip edge, plus the space above each category header row.
const PAD = 6;
export const PAD_TOP = 20;
const GAP = 3;
const MARGIN = 6;

export interface PositionedLeaf {
  x: number;
  y: number;
  w: number;
  h: number;
  leaf: AppLeaf;
  categoryColor: string;
  categoryName: string;
}

export interface PositionedCat {
  x: number;
  y: number;
  w: number;
  name: string;
}

export interface UnifiedLayout {
  kind: "unified";
  width: number;
  height: number;
  cats: PositionedCat[];
  leaves: PositionedLeaf[];
}

export interface StackedRow {
  name: string;
  color: string;
  height: number;
  leaves: PositionedLeaf[];
}

export interface StackedLayout {
  kind: "stacked";
  width: number;
  rows: StackedRow[];
}

export type TreemapLayout = UnifiedLayout | StackedLayout;

export function layoutCategory(
  cat: CategoryNode,
  width: number,
  height: number,
  paddingTop: number
): PositionedLeaf[] {
  const root = hierarchy<{ children?: unknown[]; value?: number }>(
    { children: cat.children } as { children: unknown[] },
    (d) => (d as { children?: unknown[] }).children as never[] | undefined
  )
    .sum((d) => (d as unknown as AppLeaf).value ?? 0)
    // eslint-disable-next-line unicorn/no-array-sort -- d3 HierarchyNode.sort, not Array#sort
    .sort((a, b) => (b.value ?? 0) - (a.value ?? 0));
  treemap()
    .tile(treemapResquarify)
    .size([width, height])
    .paddingTop(paddingTop)
    .paddingInner(GAP)
    .round(true)(root as never);
  return root.leaves().map((n) => {
    const node = n as unknown as {
      x0: number;
      y0: number;
      x1: number;
      y1: number;
      data: AppLeaf;
    };
    return {
      x: node.x0,
      y: node.y0,
      w: Math.max(0, node.x1 - node.x0),
      h: Math.max(0, node.y1 - node.y0),
      leaf: node.data,
      categoryColor: cat.color,
      categoryName: cat.name,
    };
  });
}

/** One treemap over all categories (desktop / tablet). */
export function computeUnifiedLayout(
  cats: CategoryNode[],
  width: number,
  height: number
): UnifiedLayout {
  const innerW = width - 2 * MARGIN;
  const innerH = height - 2 * MARGIN;

  const root = hierarchy<{ children?: unknown[]; value?: number }>(
    { children: cats } as { children: unknown[] },
    (d) => (d as { children?: unknown[] }).children as never[] | undefined
  )
    .sum((d) => (d as unknown as AppLeaf).value ?? 0)
    // eslint-disable-next-line unicorn/no-array-sort -- d3 HierarchyNode.sort, not Array#sort
    .sort((a, b) => (b.value ?? 0) - (a.value ?? 0));

  treemap()
    .tile(treemapResquarify)
    .size([innerW, innerH])
    // Header space only on the category rows (depth 1); depth 0 (root) would
    // otherwise reserve an unused empty band across the top of the chart.
    .paddingTop((d) => ((d as { depth: number }).depth === 0 ? 0 : PAD_TOP))
    .paddingInner(GAP)
    .round(true)(root as never);

  const catNodes: PositionedCat[] = (root.children ?? []).map((c) => {
    const node = c as unknown as {
      x0: number;
      x1: number;
      y0: number;
      data: CategoryNode;
    };
    return {
      x: node.x0 + MARGIN,
      y: node.y0 + MARGIN,
      w: node.x1 - node.x0,
      name: node.data.name,
    };
  });

  const leaves: PositionedLeaf[] = root.leaves().map((n) => {
    const node = n as unknown as {
      x0: number;
      y0: number;
      x1: number;
      y1: number;
      data: AppLeaf;
      parent: { data: CategoryNode } | null;
    };
    return {
      x: node.x0 + MARGIN,
      y: node.y0 + MARGIN,
      w: Math.max(0, node.x1 - node.x0),
      h: Math.max(0, node.y1 - node.y0),
      leaf: node.data,
      categoryColor: node.parent?.data.color ?? "#c7ccd6",
      categoryName: node.parent?.data.name ?? "",
    };
  });

  return { kind: "unified", width, height, cats: catNodes, leaves };
}

/**
 * Per-category stacked rows (mobile). Each category gets the full width and a
 * height scaled to its share of total adoption (clamped so even small
 * categories stay tappable and readable). Category names render as HTML headers
 * above each row, so no in-SVG header fitting is needed here.
 */
export function computeStackedLayout(
  cats: CategoryNode[],
  width: number
): StackedLayout {
  const MIN_ROW = 132;
  const MAX_ROW = 300;
  const catTotals = cats.map((c) =>
    c.children.reduce((s, l) => s + l.value, 0)
  );
  const grand = catTotals.reduce((s, v) => s + v, 0) || 1;
  // Total vertical budget scales with how many categories there are so the
  // column never collapses; individual rows are clamped for readability.
  const budget = Math.max(cats.length * MIN_ROW, width * 2.2);

  const rows: StackedRow[] = cats.map((cat, i) => {
    const height = Math.round(
      Math.min(
        MAX_ROW,
        Math.max(MIN_ROW, ((catTotals[i] ?? 0) / grand) * budget)
      )
    );
    return {
      name: cat.name,
      color: cat.color,
      height,
      leaves: layoutCategory(cat, width, height, 0),
    };
  });

  return { kind: "stacked", width, rows };
}

// --- label fitting (ported from the original D3 embed) ------------------------

export type LeafLabel =
  | { kind: "none" }
  | {
      kind: "rotated";
      name: string;
      nameFs: number;
      showSub: boolean;
      subFs: number;
    }
  | {
      kind: "horizontal";
      name: string;
      nameFs: number;
      topY: number;
      showSub: boolean;
      subFs: number;
      subY: number;
    };

function truncate(name: string, max: number): string {
  return name.length > max
    ? name.slice(0, Math.max(1, max - 1)).trim() + "…"
    : name;
}

/** Decide how (and whether) an app label fits inside a w×h block. */
export function fitLeafLabel(w: number, h: number): LeafLabel {
  if (w < 16 || h < 16) return { kind: "none" };

  // Tall & narrow cell that can't fit horizontal text -> rotate 90°.
  if (w < 60 && h >= 46 && h > w * 1.25) {
    const nameFs = w > 30 ? 11 : 9.5;
    const twoLines = w >= 24;
    const showSub = twoLines && w >= nameFs + (nameFs - 1.5) + PAD + 4;
    return { kind: "rotated", name: "", nameFs, showSub, subFs: nameFs - 1.5 };
  }

  if (w < 30 || h < 14) return { kind: "none" };

  const big = w > 78 && h > 46;
  const nameFs = big ? 13 : h < 20 ? 9 : 10.5;
  const topY = Math.min(13, h - 3);
  const showSub = h >= 30;
  return {
    kind: "horizontal",
    name: "",
    nameFs,
    topY,
    showSub,
    subFs: big ? 11 : 9.5,
    subY: big ? 30 : 26,
  };
}

/** Truncate an app name to the width available for a horizontal label. */
export function fitHorizontalName(
  name: string,
  w: number,
  nameFs: number
): string {
  const charW = nameFs * 0.58;
  const usable = w - 4 - PAD;
  return truncate(name, Math.max(2, Math.floor(usable / charW)));
}

/** Truncate an app name to the height available for a rotated label. */
export function fitRotatedName(
  name: string,
  h: number,
  nameFs: number
): string {
  const usableLen = h - 6 - PAD;
  return truncate(name, Math.max(2, Math.floor(usableLen / (nameFs * 0.62))));
}

export interface CatHeader {
  lines: { text: string; dy: number }[];
  fontSize: number;
}

/** Fit a category header into its block width, wrapping to two lines if needed. */
export function fitCatHeader(name: string, w: number): CatHeader {
  const upper = name.toUpperCase();
  const avail = w - 3 - PAD;
  const CHAR = 0.66;
  const MAX_FS = 12;
  const MIN_FS = 7;
  const fs = Math.min(MAX_FS, avail / (upper.length * CHAR));
  if (fs >= MIN_FS) {
    return { lines: [{ text: upper, dy: 13 }], fontSize: +fs.toFixed(1) };
  }
  const maxChars = Math.max(3, Math.floor(avail / (MIN_FS * CHAR)));
  const words = upper.split(" ");
  let l1 = "";
  let l2 = "";
  for (const wd of words) {
    if (!l1 || ((l1 + " " + wd).length <= maxChars && !l2)) {
      l1 = l1 ? l1 + " " + wd : wd;
    } else {
      l2 = l2 ? l2 + " " + wd : wd;
    }
  }
  const lines = [{ text: l1, dy: 11 }];
  if (l2) lines.push({ text: l2, dy: 21 });
  return { lines, fontSize: MIN_FS };
}
